import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePermission } from '@/hooks/usePermission';
import { Forbidden } from '@/components/common/Forbidden';
import type { UserRole } from '@/types';
import type { Permission } from '@/auth/permissions';

interface ProtectedRouteProps {
  /** Roles allowed to access this route. If empty, any authenticated user is allowed. */
  allowedRoles?: UserRole[];
  /**
   * Fine-grained permission the route requires. When the user is authenticated
   * but lacks it, the {@link Forbidden} page renders IN PLACE (no redirect) so
   * the URL stays put and the user understands why.
   */
  requiredPermission?: Permission;
  children: React.ReactNode;
}

/**
 * Wraps a route that requires authentication (and optionally a specific role
 * or permission).
 * - Not authenticated -> redirect to /login
 * - Authenticated but wrong role (allowedRoles) -> redirect to /
 * - Authenticated but missing requiredPermission -> render <Forbidden /> in place
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  requiredPermission,
  children,
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const { hasPermission } = usePermission();
  const location = useLocation();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Forbidden />;
  }

  return <>{children}</>;
};
